import { showSnackbar } from "@/stores/snackbar";
import client from "@/utils/api/client";
import { useCallback, useState } from "react";
import useAnalytics from "./useAnalytics";

type UseFollowReturn = {
  followHandler: VoidFunction;
  status: FollowStatus;
};

export enum FollowStatus {
  Default,
  Progress,
}

export type FollowableEntity = {
  id: number;
  is_following: boolean;
  followers_count?: number;
};

export type FollowMutateHandler<T> = (updatedData: T) => void;

const useFollow = <T extends FollowableEntity>(
  user: T,
  onUpdated: FollowMutateHandler<T>,
): UseFollowReturn => {
  const { event } = useAnalytics();
  const [status, setStatus] = useState<FollowStatus>(FollowStatus.Default);

  const followHandler = useCallback(async () => {
    if (status === FollowStatus.Progress) return;

    try {
      setStatus(FollowStatus.Progress);

      if (user.is_following) {
        await client.followsUserIdDelete({ userId: user.id });
        event("unfollow", { item_id: `U_${user.id}` });

        const updated = { ...user, is_following: false };

        if ("followers_count" in user) {
          updated.followers_count = user.followers_count - 1;
        }
        onUpdated(updated);
      } else {
        await client.followsPost({
          followCreateParameter: { user_id: user.id },
        });
        event("follow", { item_id: `U_${user.id}` });

        const updated = { ...user, is_following: true };

        if ("followers_count" in user) {
          updated.followers_count = user.followers_count + 1;
        }
        onUpdated(updated);
      }

      setStatus(FollowStatus.Default);
    } catch {
      showSnackbar({
        message: "Likeに失敗しました",
        severity: "error",
      });
      setStatus(FollowStatus.Default);
    }
  }, [user, status, onUpdated, event]);

  return {
    followHandler,
    status,
  };
};
export default useFollow;
